import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useAuth } from '../../context/AuthContext';
import { createPortalSession, createCheckoutSession, fetchPricing } from '../../services/stripeService';
import './SubscriptionModal.css';

// Shown until the live fetch resolves (or if it fails) — today's actual prices,
// so there's never a blank/broken display while Stripe is one round-trip away.
const FALLBACK_PRICING = {
  monthly: { amount: 1.99, currency: 'AUD', interval: 'month' },
  yearly:  { amount: 9.99, currency: 'AUD', interval: 'year' },
};
const fmtPrice = p => (p ? `$${p.amount.toFixed(2)}` : '—');

function RefreshIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M23 4v6h-6"/><path d="M1 20v-6h6"/>
      <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/>
    </svg>
  );
}

const STATUS_LABEL = {
  active:   { text: 'Active',    cls: 'sub-modal__status--active'   },
  trialing: { text: 'Trial',     cls: 'sub-modal__status--trial'    },
  past_due: { text: 'Past due',  cls: 'sub-modal__status--past-due' },
  cancelled:{ text: 'Cancelled', cls: 'sub-modal__status--cancelled'},
};

export function SubscriptionModal({ onClose }) {
  const { user, claims, subscription, isSubscribed, refreshSubscription } = useAuth();
  const [loading, setLoading]     = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError]         = useState('');
  const [selectedPlan, setSelectedPlan] = useState('yearly');
  const [pricing, setPricing]     = useState(FALLBACK_PRICING);

  // Live from Stripe — keeps the fallback (today's real prices) if this fails,
  // so a network hiccup never shows something broken.
  useEffect(() => {
    fetchPricing()
      .then(data => setPricing(prev => ({ ...prev, ...data })))
      .catch(() => {});
  }, []);

  // Prefer claims (always fresh after sync) then Firestore
  const status    = claims?.subscriptionStatus || subscription?.subscriptionStatus || null;
  const plan      = claims?.subscriptionPlan   || subscription?.subscriptionPlan   || null;
  const statusInfo = STATUS_LABEL[status] ?? null;

  async function handleRefresh() {
    setRefreshing(true);
    setError('');
    try {
      await refreshSubscription();
    } catch (err) {
      setError(err.message);
    } finally {
      setRefreshing(false);
    }
  }

  async function openPortal() {
    setLoading(true);
    setError('');
    try {
      const url = await createPortalSession();
      window.location.href = url;
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  }

  async function openCheckout(plan) {
    setLoading(true);
    setError('');
    try {
      const url = await createCheckoutSession(plan);
      window.location.href = url;
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  }

  return createPortal(
    <div className="sub-modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="sub-modal">
        {/* Header */}
        <div className="sub-modal__header">
          <div className="sub-modal__header-title">
            <span className="sub-modal__icon">⚡</span>
            Subscription
          </div>
          <button className="sub-modal__refresh" onClick={handleRefresh} disabled={refreshing} aria-label="Refresh subscription status" title="Sync from Stripe">
            {refreshing ? <span className="sub-modal__spinner sub-modal__spinner--sm" /> : <RefreshIcon />}
          </button>
          <button className="sub-modal__close" onClick={onClose} aria-label="Close">
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <path d="M1 1l10 10M11 1L1 11" />
            </svg>
          </button>
        </div>

        <div className="sub-modal__body">
          {isSubscribed ? (
            <>
              {/* Plan card */}
              <div className="sub-modal__plan-card">
                <div className="sub-modal__plan-row">
                  <div>
                    <div className="sub-modal__plan-name">
                      Motoroam Pro
                      {plan && <span className="sub-modal__plan-cycle">· {plan === 'yearly' ? 'Yearly' : 'Monthly'}</span>}
                    </div>
                    {statusInfo && (
                      <span className={`sub-modal__status ${statusInfo.cls}`}>{statusInfo.text}</span>
                    )}
                  </div>
                  <div className="sub-modal__plan-price">
                    {plan === 'yearly' ? (
                      <><span className="sub-modal__price-amount">{fmtPrice(pricing.yearly)}</span><span className="sub-modal__price-per">/yr</span></>
                    ) : (
                      <><span className="sub-modal__price-amount">{fmtPrice(pricing.monthly)}</span><span className="sub-modal__price-per">/mo</span></>
                    )}
                    <span className="sub-modal__price-currency">{(plan === 'yearly' ? pricing.yearly : pricing.monthly)?.currency || 'AUD'}</span>
                  </div>
                </div>

              </div>

              {/* Features */}
              <ul className="sub-modal__features">
                {['Motorcycle-tuned routing', 'Smarter route generation', 'Weather & POIs along your route', 'Elevation-baked GPX', 'Priority access to new features'].map(f => (
                  <li key={f} className="sub-modal__feature">
                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="#4f46e5" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M2 7l3.5 3.5L12 3"/>
                    </svg>
                    {f}
                  </li>
                ))}
              </ul>

              {error && <div className="sub-modal__error">{error}</div>}

              {/* Manage via Stripe portal */}
              <button className="sub-modal__portal-btn" onClick={openPortal} disabled={loading}>
                {loading
                  ? <span className="sub-modal__spinner" />
                  : <>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/>
                        <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/>
                      </svg>
                      Manage billing &amp; subscription
                    </>
                }
              </button>
              <p className="sub-modal__portal-hint">
                Update payment method, switch plans, or cancel — all via the secure Stripe portal.
              </p>
            </>
          ) : (
            <>
              {/* Not subscribed */}
              <div className="sub-modal__free-hero">
                <div className="sub-modal__free-icon">🗺</div>
                <div className="sub-modal__free-title">You're on the free plan</div>
                <div className="sub-modal__free-sub">Upgrade to Motoroam Pro to unlock all features</div>
              </div>

              <div className="sub-modal__upgrade-options">
                <button
                  className={`sub-modal__upgrade-btn${selectedPlan === 'monthly' ? ' sub-modal__upgrade-btn--selected' : ''}`}
                  onClick={() => setSelectedPlan('monthly')}
                  disabled={loading}
                >
                  <span className="sub-modal__upgrade-label">Monthly</span>
                  <span className="sub-modal__upgrade-price">{fmtPrice(pricing.monthly)} {pricing.monthly?.currency || 'AUD'}/mo</span>
                </button>
                <div className="sub-modal__upgrade-cell">
                  {/* Sibling of the button, not a child — themes that clip-path the
                      button (e.g. Auris's chamfered corners) would otherwise slice
                      off the top of this badge, since it deliberately pokes above
                      the button's own box. */}
                  <span className="sub-modal__popular-badge">Best value</span>
                  <button
                    className={`sub-modal__upgrade-btn sub-modal__upgrade-btn--popular${selectedPlan === 'yearly' ? ' sub-modal__upgrade-btn--selected' : ''}`}
                    onClick={() => setSelectedPlan('yearly')}
                    disabled={loading}
                  >
                    <span className="sub-modal__upgrade-label">Yearly</span>
                    <span className="sub-modal__upgrade-price">{fmtPrice(pricing.yearly)} {pricing.yearly?.currency || 'AUD'}/yr</span>
                  </button>
                </div>
              </div>

              {error && <div className="sub-modal__error">{error}</div>}

              <button className="sub-modal__start-btn" onClick={() => openCheckout(selectedPlan)} disabled={loading}>
                {loading ? <span className="sub-modal__spinner sub-modal__spinner--white" /> : 'Start Pro'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}
